import axios from "axios";
import { BACKEND_URL } from "../utils/envConfig";

const OTP_API_URL = BACKEND_URL;

/** Patient portal test OTP while Twilio is not subscribed. */
export const DEV_PATIENT_OTP = "1234";

export const isDevPatientOtp = (otp) => String(otp ?? "").trim() === DEV_PATIENT_OTP;

/**
 * Generates and sends OTP to the given mobile number via SMS (Twilio)
 * @param {string} mobile - Mobile number to send OTP to
 * @param {{ allowDevPatientOtp?: boolean }} [options]
 * @returns {Promise<{success: boolean, message?: string, usedDevFallback?: boolean}>}
 */
export const generateAndSendOTP = async (mobile, options = {}) => {
  if (!mobile) {
    throw new Error("Mobile number is required");
  }

  try {
    const response = await axios.post(`${OTP_API_URL}/otp/generate`, {
      mobile,
    });
    return {
      success: true,
      message: response.data?.message || "OTP sent successfully",
      usedDevFallback: false,
    };
  } catch (error) {
    if (options.allowDevPatientOtp) {
      console.warn("OTP SMS unavailable; using patient test OTP 1234.", error);
      return {
        success: true,
        message: "Use test OTP 1234 until SMS is configured.",
        usedDevFallback: true,
      };
    }

    console.error("Error generating OTP:", error);
    throw new Error(
      error?.response?.data?.message ||
      error?.response?.data ||
      "Failed to send OTP. Please try again."
    );
  }
};

/**
 * Verifies the OTP for the given mobile number
 * @param {string} mobile - Mobile number
 * @param {string} otp - OTP code to verify
 * @param {number|null} organizationId - Organization ID (optional, for validation)
 * @returns {Promise<{success: boolean, message?: string, token?: string, user?: object}>}
 */
export const verifyOTP = async (mobile, otp, organizationId = null) => {
  if (!mobile || !otp) {
    throw new Error("Mobile number and OTP are required");
  }

  try {
    const requestBody = { mobile, otp };
    if (organizationId !== null && organizationId !== undefined) {
      requestBody.organizationId = organizationId;
    }
    
    const response = await axios.post(`${OTP_API_URL}/otp/verify`, requestBody);
    return {
      success: true,
      message: response.data?.message || "OTP verified successfully",
      token: response.data?.token,
      user: response.data?.user,
    };
  } catch (error) {
    console.error("Error verifying OTP:", error);
    throw new Error(
      error?.response?.data?.message ||
      error?.response?.data ||
      "Invalid OTP. Please try again."
    );
  }
};

/**
 * Login with OTP (generates, sends, and returns verification result)
 * This is a convenience method that combines generate and verify
 * @param {string} mobile - Mobile number
 * @param {string} otp - OTP code
 * @param {number|null} organizationId - Organization ID (optional, for validation)
 * @returns {Promise<{token: string, user: object}>}
 */
export const loginWithOTP = async (mobile, otp, organizationId = null) => {
  const result = await verifyOTP(mobile, otp, organizationId);
  if (!result.success || !result.token || !result.user) {
    throw new Error("OTP verification failed");
  }
  return {
    token: result.token,
    user: result.user,
  };
};

